import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Archive, Shield, Users, Lock, FileText, Clock } from 'lucide-react';
import SEO from '../SEO';
import BackgroundAnimations from './BackgroundAnimations';
import './styles.css';

// Hook: observe element and add 'visible' class when it enters viewport
function useScrollReveal(options = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('revealed');
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px', ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

const serviceColumns = [
  {
    title: 'CUSTODIAL RECORDS',
    cards: [
      {
        title: 'Documents',
        desc: 'Family records, legal documents, and legacy materials',
        icon: FileText
      },
      {
        title: 'Memories',
        desc: 'Photos, videos, and personal histories preserved',
        icon: Archive
      }
    ]
  },
  {
    title: 'CONTINGENCY PROTOCOLS',
    cards: [
      {
        title: 'Guardian Access',
        desc: 'Time-delayed access for designated guardians',
        icon: Shield
      },
      {
        title: 'Fallback Authority',
        desc: 'Continuity enforcement under incapacitation',
        icon: Lock
      }
    ]
  },
  {
    title: 'GOVERNANCE STRUCTURE',
    cards: [
      {
        title: 'Family Tree',
        desc: 'Authority mapping and lineage governance',
        icon: Users
      },
      {
        title: 'Succession',
        desc: 'Role inheritance and access transitions',
        icon: Clock
      }
    ]
  }
];

const differentiators = [
  'Zero-Knowledge Encryption',
  'Automated Succession',
  'Multi-Generational Governance',
  'Institutional-Grade Security'
];

const audienceSegments = [
  {
    label: 'Family Offices',
    description:
      'Continuity OS™ so governance, mandates, and execution do not collapse when principals or key staff change.'
  },
  {
    label: 'Ultra-High-Net-Worth Families',
    description:
      'Coherent authority mapping, verified heirs, and conditional unlocks so intent survives across generations.'
  },
  {
    label: 'Advisors & Trustees',
    description:
      'A neutral, zero-knowledge execution layer that enforces what was agreed instead of relying on email, memory, or staff turnover.'
  }
];

const howItWorksSteps = [
  {
    step: '01',
    title: 'Model the family',
    description:
      'Map principals, heirs, trustees, and advisors into a live authority graph instead of a static PDF.'
  },
  {
    step: '02',
    title: 'Attach continuity protocols',
    description:
      'Define what should unlock, for whom, and under which verified conditions – incapacity, death, time-delay, or multi-party approval.'
  },
  {
    step: '03',
    title: 'Ingest documents & memories',
    description:
      'Place documents, records, and narrative assets inside Continuity Domains™ tied to the governance model – not loose folders.'
  },
  {
    step: '04',
    title: 'Let the system execute',
    description:
      'When conditions are met, the infrastructure enforces access pathways automatically, with auditability and no unilateral override.'
  }
];

const securityPillars = [
  {
    title: 'Zero-knowledge by design',
    description:
      'Encryption keys never leave your devices; our infrastructure orchestrates encrypted blobs and policies, not readable content.'
  },
  {
    title: 'Verified emergency access',
    description:
      'Multi-channel checks and time-delayed unlocks reduce the risk of coerced access or rushed decisions at the wrong moment.'
  },
  {
    title: 'Institutional-grade posture',
    description:
      'Modern cryptography, segregated environments, and rigorous operational controls designed for institutional families and offices.'
  }
];

function RevealOnScroll({ children, className = '', delay = 0 }) {
  const ref = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

function EternaVault() {
  const { isAuthenticated } = useAuth0();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [heroZoomProgress, setHeroZoomProgress] = useState(0);
  const [servicesVisible, setServicesVisible] = useState(false);
  const [vaultOpened, setVaultOpened] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const containerRef = useRef(null);
  const servicesRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const progress = Math.min(scrollTop / (windowHeight * 2), 1);

      setScrollProgress(progress);

      // Heavy zoom: 0→1 over first ~0.7 viewport of scroll, then transition to next layer
      const zoomRange = windowHeight * 0.7;
      const rawZoom = Math.min(scrollTop / zoomRange, 1);
      const easedZoom = Math.pow(rawZoom, 1.4); // heavy ease-in
      setHeroZoomProgress(easedZoom);

      setVaultOpened(rawZoom > 0.15);
      setShowBackToTop(scrollTop > windowHeight * 0.5);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // initial
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setServicesVisible(true);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    if (servicesRef.current) observer.observe(servicesRef.current);
    return () => observer.disconnect();
  }, []);

  const backgroundOpacity = Math.min(scrollProgress * 1.2, 0.85);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'EternaVaults',
    description: 'Category creator in continuity architecture and governance infrastructure for ultra-high-net-worth families',
    url: 'https://eternavaults.com',
    serviceType: ['Continuity Architecture', 'Governance Infrastructure', 'Authority & Decision Architecture'],
    knowsAbout: ['Family Office Governance', 'Authority Architecture', 'Decision Survivability', 'UHNWI Infrastructure']
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Continuity Architecture?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Continuity Architecture™ is the discipline of designing how governance, authority, and decisions persist across time and generational transitions. Unlike succession planning, it ensures governance structures survive with coherence and enforceability.'
        }
      },
      {
        '@type': 'Question',
        name: 'How is governance infrastructure different from wealth management platforms?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Wealth management platforms track portfolios. Governance infrastructure enforces decision-making authority and continuity protocols across generational change, ensuring governance survives when decision-makers become unavailable.'
        }
      },
      {
        '@type': 'Question',
        name: 'How is this different from a digital vault?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Digital vaults store documents. Governance infrastructure enforces continuity protocols and authority pathways. Storage preserves information; infrastructure preserves authority and executes governance decisions automatically.'
        }
      }
    ]
  };

  return (
    <>
      <SEO
        title="EternaVaults | Governance Infrastructure for Family Offices & UHNWIs"
        description="Continuity Architecture™ for multi-generational wealth. Engineering how authority, decisions, and governance survive transition, absence, and generational change. Beyond portfolio management - institutional governance infrastructure for family offices."
        keywords="family office governance infrastructure, UHNWI continuity architecture, governance infrastructure platform, multi-generational wealth governance, continuity architecture family offices, decision survivability framework, authority architecture, alternative to Addepar for governance, trust governance infrastructure"
        canonical="https://eternavaults.com/"
        ogImage="/home-og-image.jpg"
        structuredData={[organizationSchema, faqSchema]}
      />

      <div className="vault-scroll-container" ref={containerRef}>
        <div
          className="vault-background-overlay"
          style={{
            opacity: backgroundOpacity,
            background: `linear-gradient(180deg,
              rgba(28, 20, 30, ${backgroundOpacity * 0.6}) 0%,
              rgba(28, 20, 30, ${backgroundOpacity}) 100%)`
          }}
        />

        <BackgroundAnimations />

        {/* Hero Section — unified vault: 3D tilt + heavy zoom (door opens toward you), then next layer */}
        <section ref={heroRef} className={`vault-hero-section ${vaultOpened ? 'vault-opening' : ''}`}>
          <div
            className="vault-door-static"
            style={{
              transform: `
                rotateY(${heroZoomProgress * -14}deg)
                scale(${1 + heroZoomProgress * 1.5})
              `,
              opacity: 1 - heroZoomProgress * 0.4,
              transition: 'none'
            }}
          >
            <div
              className="vault-texture"
              style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/vault-texture.jpg)` }}
            />

            <div
              className="scroll-to-services"
              style={{ opacity: Math.max(0, 1 - heroZoomProgress * 1.5) }}
            >
              <div className="scroll-arrow-container">
                <div className="scroll-text">Scroll to explore</div>
                <div className="scroll-arrow">↓</div>
              </div>
            </div>

            <div
              className="vault-text"
              style={{
                opacity: Math.max(0, 1 - heroZoomProgress * 1.3),
                transform: `scale(${Math.max(0.7, 1 - heroZoomProgress * 0.2)})`
              }}
            >
              <h1>EternaVaults</h1>
              <p className="main-tagline">Digital Continuity Infrastructure™</p>
              <p className="sub-tagline">Continuity Domains™. Automated succession.<br /> Sovereign-grade enforcement.</p>
              <p className="plainlanguage-tagline">
                In plain language: EternaVaults is the execution layer that makes sure the right people can act – with the right documents and authority – when you are no longer available to explain it.
              </p>
            </div>
          </div>

          {!isAuthenticated && (
            <button
              aria-label="Create secure family digital vault"
              className="vault-cta-button-overlay"
              style={{ opacity: Math.max(0, 1 - heroZoomProgress * 1.5) }}
              onClick={() => window.location.href = '/contact'}
            >
              Request Private Access
            </button>
          )}

          <div className="hero-bottom-fade" />
        </section>

        {/* Consolidated Services Section */}
        <section
          ref={servicesRef}
          className={`vault-services-consolidated ${servicesVisible ? 'visible' : ''}`}
        >
          <div className="vault-services-glow" />
          <div className="vault-services-grid">
            {serviceColumns.map((col, colIndex) => (
              <div
                key={col.title}
                className={`vault-service-column col-${colIndex}`}
              >
                <h2 className="vault-column-title">{col.title}</h2>
                <div className="vault-column-cards">
                  {col.cards.map((card, cardIndex) => (
                    <div
                      key={card.title}
                      className={`vault-card card-${colIndex}-${cardIndex}`}
                    >
                      <div className="vault-card-glow" />
                      <div className="vault-card-inner">
                        <div className="vault-card-icon">
                          <card.icon size={32} />
                        </div>
                        <h3 className="vault-card-title">{card.title}</h3>
                        <p className="vault-card-desc">{card.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Audience & How It Works */}
        <section className="vault-explainer-section">
          <div className="vault-explainer-inner">
            <RevealOnScroll className="vault-explainer-header">
              <h2 className="vault-explainer-title">Built for families who think in generations.</h2>
              <p className="vault-explainer-copy">
                Continuity infrastructure is not a document vault or password manager. It is the governance engine that
                keeps your family office, mandates, and decision pathways coherent when principals, advisors, or
                generations change.
              </p>
            </RevealOnScroll>

            <div className="vault-explainer-grid">
              <RevealOnScroll className="vault-explainer-column">
                <h3 className="vault-explainer-subtitle">Who it&apos;s for</h3>
                <div className="vault-audience-list">
                  {audienceSegments.map(segment => (
                    <div key={segment.label} className="vault-audience-item">
                      <div className="vault-audience-label">{segment.label}</div>
                      <p className="vault-audience-description">{segment.description}</p>
                    </div>
                  ))}
                </div>
              </RevealOnScroll>

              <RevealOnScroll className="vault-explainer-column" delay={0.15}>
                <h3 className="vault-explainer-subtitle">How it works</h3>
                <ol className="vault-how-steps">
                  {howItWorksSteps.map(step => (
                    <li key={step.step} className="vault-how-step">
                      <div className="vault-how-step-number">{step.step}</div>
                      <div className="vault-how-step-body">
                        <div className="vault-how-step-title">{step.title}</div>
                        <p className="vault-how-step-description">{step.description}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </RevealOnScroll>
            </div>

            <RevealOnScroll delay={0.25}>
              <div className="vault-advisors-note">
                <div className="vault-advisors-note-heading">For advisors and trustees</div>
                <p className="vault-advisors-note-copy">
                  Continuity and succession are not one-off projects. EternaVaults gives you an enduring, operational
                  relationship with the families you serve: every future incapacity, leadership transition, or successor
                  onboarding runs through the same execution layer you helped design.
                  <button
                    type="button"
                    className="vault-advisors-link"
                    onClick={() => window.location.href = '/for-advisors'}
                  >
                    Learn more on the advisors page
                  </button>
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        {/* Security & Sovereignty */}
        <section className="vault-security-section">
          <div className="vault-security-inner">
            <RevealOnScroll>
              <h2 className="vault-security-title">Security, sovereignty, and execution in the same system.</h2>
            </RevealOnScroll>
            <div className="vault-security-grid">
              {securityPillars.map((pillar, i) => (
                <RevealOnScroll key={pillar.title} className="vault-security-card" delay={i * 0.12}>
                  <h3 className="vault-security-card-title">{pillar.title}</h3>
                  <p className="vault-security-card-description">{pillar.description}</p>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Final Section */}
        <section className="vault-final-section">
          <div className="vault-final-content">
            <RevealOnScroll>
              <div className="vault-differentiators">
                {differentiators.map(d => (
                  <div key={d} className="vault-differentiator">
                    {d}
                  </div>
                ))}
              </div>
            </RevealOnScroll>

            <RevealOnScroll delay={0.12}>
              <h1 className="vault-final-title">
                CONTINUITY
                <br />
                INFRASTRUCTURE™
              </h1>
            </RevealOnScroll>

            <RevealOnScroll delay={0.24}>
              <button
                className="vault-get-in-touch-button"
                onClick={() => window.location.href = '/contact'}
              >
                Request Private Access
              </button>
            </RevealOnScroll>
          </div>
        </section>

        {showBackToTop && (
          <button
            className="back-to-top-button"
            onClick={scrollToTop}
            aria-label="Back to top"
          >
            ↑
          </button>
        )}
      </div>
    </>
  );
}

export default EternaVault;
